"use client";

/**
 * App lifecycle bridge.
 *
 * Native-first concern: Capacitor's WebView does not fire the window `focus`
 * events that TanStack Query's default focus listener relies on, and the OS
 * suspends timers/polling while the app is backgrounded. That means queries
 * with `refetchOnWindowFocus: true` never refetch on native resume, and
 * feature state can look frozen when the app returns to the foreground.
 *
 * This module wires the authoritative native signal (`@capacitor/app`
 * `appStateChange`) — plus the web `visibilitychange` fallback — into:
 *   1. TanStack Query's `focusManager`, so `refetchOnWindowFocus` queries
 *      (e.g. chat) refetch the moment the app resumes; and
 *   2. a small foreground event bus that feature hooks subscribe to via
 *      `useAppForeground` for custom resync logic (e.g. movement re-hydration).
 */

import { Capacitor } from "@capacitor/core";
import { focusManager } from "@tanstack/react-query";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("lib/app-lifecycle");

type ForegroundListener = () => void;

const foregroundListeners = new Set<ForegroundListener>();
const SERVICE_WORKER_UPDATE_INTERVAL_MS = 5 * 60 * 1000;
let lastServiceWorkerUpdateAt = 0;

/**
 * Subscribe to "app returned to foreground" transitions. Fires once per
 * background→foreground transition (native resume or web tab becoming
 * visible). Returns an unsubscribe function.
 */
export function subscribeForeground(listener: ForegroundListener): () => void {
  foregroundListeners.add(listener);
  return () => {
    foregroundListeners.delete(listener);
  };
}

/**
 * Ask an installed service worker to check for a newer deployment after an
 * app resume. iOS standalone apps can keep an old WebKit process alive for a
 * long time; updating here prevents a resumed client from staying on stale
 * RSC/client assets until the app is force-quit.
 */
export function refreshServiceWorkerOnForeground(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  const now = Date.now();
  if (now - lastServiceWorkerUpdateAt < SERVICE_WORKER_UPDATE_INTERVAL_MS) {
    return;
  }
  lastServiceWorkerUpdateAt = now;

  void navigator.serviceWorker
    .getRegistration("/")
    .then((registration) => registration?.update())
    .catch((error: unknown) => {
      logger.debug("app_lifecycle.service_worker_update_failed", {
        message: error instanceof Error ? error.message : String(error),
      });
    });
}

function emitForeground(): void {
  for (const listener of [...foregroundListeners]) {
    try {
      listener();
    } catch (error) {
      logger.warn("app_lifecycle.foreground_listener_failed", {
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

let initialized = false;

/**
 * Install the lifecycle bridge. Idempotent and SSR-safe — safe to call from
 * multiple mounts or under React StrictMode.
 */
export function setupAppLifecycle(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  focusManager.setEventListener((handleFocus) => {
    // Seed from the current state: the app launches in the foreground, so the
    // initial "visible"/"active" event is a no-op. We only react to genuine
    // background→foreground (and →background) *transitions* — never on the
    // startup signal, which could otherwise fire a refetch/resync during the
    // first hydration pass and trip React's rules-of-hooks.
    let lastFocused =
      typeof document === "undefined"
        ? true
        : document.visibilityState === "visible";

    const setFocused = (focused: boolean) => {
      if (focused === lastFocused) return;
      lastFocused = focused;
      handleFocus(focused);
      // Session boundaries. Without these, a native session looks like one
      // unbroken visit from install to uninstall, and "how often do people
      // come back" has no answer.
      captureClientEvent(
        focused
          ? ANALYTICS_EVENTS.APP_FOREGROUNDED
          : ANALYTICS_EVENTS.APP_BACKGROUNDED,
        {
          online:
            typeof navigator === "undefined" ? undefined : navigator.onLine,
        },
      );
      if (focused) emitForeground();
    };

    const onOnline = () =>
      captureClientEvent(ANALYTICS_EVENTS.APP_ONLINE_RESTORED, {});
    const onOffline = () =>
      captureClientEvent(ANALYTICS_EVENTS.APP_OFFLINE_DETECTED, {});

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      setFocused(document.visibilityState === "visible");
    };

    let removeNative: (() => void) | undefined;

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange, false);
    }

    if (Capacitor.isNativePlatform()) {
      void import("@capacitor/app")
        .then(({ App }) =>
          App.addListener("appStateChange", ({ isActive }) => {
            setFocused(isActive);
          }),
        )
        .then((handle) => {
          removeNative = () => void handle.remove();
        })
        .catch((error: unknown) => {
          logger.warn("app_lifecycle.app_plugin_unavailable", {
            message: error instanceof Error ? error.message : String(error),
          });
        });
    }

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      removeNative?.();
    };
  });
}
